/**
 * Format bytes as human-readable text with an optional forced unit.
 * If the unit is not forced, the function will use the most appropriate unit.
 * If forced unit is not known, an error will be thrown.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 * @param forcedUnit Optional unit to force the output to. Must be one of 'B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'.
 * @throws {Error} Invalid forced unit.
 *
 * @return Formatted string.
 */

export function humanizedFileSize(
  bytes: number | undefined | null,
  si = true,
  dp = 1,
  forcedUnit?: string,
) {
  if (bytes == null) return '0 B';

  const thresh = si ? 1000 : 1024;
  if (Math.abs(bytes) < thresh || forcedUnit === 'B') {
    return `${bytes} B`;
  }
  const units = si
    ? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;
  let newValue = bytes;

  do {
    newValue /= thresh;
    ++u;
  } while (Math.round(Math.abs(newValue) * r) / r >= thresh && u < units.length - 1);

  // If a forced unit is specified, adjust the value and unit index accordingly
  if (forcedUnit) {
    const forcedUnitIndex = units.indexOf(forcedUnit);
    if (forcedUnitIndex === -1) throw new Error(`Invalid forced unit: ${forcedUnit}`);

    newValue = bytes / thresh ** (forcedUnitIndex + 1);
    u = forcedUnitIndex;
  }

  return `${newValue.toFixed(dp)} ${units[u]}`;
}
